import React from "react";
import {
  TRIAL_PURCHASE_ADMIN_ONLY_MESSAGE,
  TRIAL_PURCHASE_CTA_LABEL,
} from "../../utils/trialPurchase";

const SUMMARY_ID = "trial-purchase-summary";
const ADMIN_ONLY_ID = "trial-purchase-admin-only";

/** What the purchase is for, as shown under the prompt and read with the button. */
export interface TrialPurchaseSummary {
  /** Billing interval being bought, e.g. "kwartalny". */
  intervalLabel: string;
  /** Gross amount, or `null` when the preview is priced for another interval. */
  grossAmount: string | null;
}

interface TrialPurchasePromptProps {
  summary: TrialPurchaseSummary;
  /** The preview is being recalculated: no summary, no purchase. */
  isPricing: boolean;
  isSubmitting: boolean;
  /** Only a tenant admin may buy; anyone else sees why the button is disabled. */
  canPurchase: boolean;
  onPurchase: () => void;
  buttonRef: React.Ref<HTMLButtonElement>;
}

/**
 * 5c: the "Kupuję — wystaw proformę" call to action of a trial without
 * automatic proforma (AccountSubscription, trialing section).
 *
 * @see docs/specifications/2026-09-30-trial-invite-bez-proformy/spec.md § 5c
 */
export const TrialPurchasePrompt: React.FC<TrialPurchasePromptProps> = ({
  summary,
  isPricing,
  isSubmitting,
  canPurchase,
  onPurchase,
  buttonRef,
}) => (
  <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg space-y-2">
    <p className="text-sm text-primary-800">
      Chcesz korzystać dalej? Wystawimy proformę na wybrany
      interwał, a po opłaceniu subskrypcja będzie się
      odnawiać automatycznie.
    </p>
    {!isPricing && (
      <p id={SUMMARY_ID} className="text-sm text-primary-800">
        Wybrany interwał:{" "}
        <span className="font-semibold">{summary.intervalLabel}</span>
        {summary.grossAmount !== null && (
          <>
            , do zapłaty{" "}
            <span className="font-semibold">{summary.grossAmount}</span>{" "}
            brutto
          </>
        )}
        .
      </p>
    )}
    <button
      ref={buttonRef}
      onClick={onPurchase}
      disabled={isSubmitting || isPricing || !canPurchase}
      aria-busy={isSubmitting}
      aria-describedby={canPurchase ? SUMMARY_ID : `${SUMMARY_ID} ${ADMIN_ONLY_ID}`}
      data-testid="trial-purchase-button"
      className="px-5 py-2 bg-primary-700 text-white text-sm font-medium rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isSubmitting ? "Składam zamówienie..." : TRIAL_PURCHASE_CTA_LABEL}
    </button>
    {!canPurchase && (
      <p id={ADMIN_ONLY_ID} className="text-sm text-gray-600">
        {TRIAL_PURCHASE_ADMIN_ONLY_MESSAGE}
      </p>
    )}
  </div>
);
